import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core'
import { Router } from '@angular/router'
import moment from 'moment'
import { fadeIn } from '../../../animations/fadeIn'
import { slideInOnlyUpStaggered } from '../../../animations/slideInOnlyUpStaggered'
import { AppRoutingConfig } from '../../../configuration/app.routing.config'
import { ConfirmDialogComponent } from '../../../dialogs/confirm-dialog/confirm-dialog.component'
import { NewStructureDialogComponent } from '../../../dialogs/new-structure-dialog/new-structure-dialog.component'
import { KeySystem, Structure } from '../../../models/dev.test.models'
import { DialogService } from '../../../services/dialog.service'
import { ErrorService } from '../../../services/error.service'
import { KeyingStructureService, ReportName } from '../../../services/keying.structure.service'
import { KeyingSystemService } from '../../../services/keying.system.service'
import { Accept, ReportsService } from '../../../services/reports.service'
import { LoadingService } from '../../../services/loading.service'
import { take, takeUntil } from 'rxjs/operators'
import { KeyingReportsService } from '../../../services/keying.reports.service'
import { User } from '@auth0/auth0-spa-js'
import { Subject, Subscription, interval } from 'rxjs'
import { environment } from '../../../../environments/environment'
import { ToastService } from '@overtur-monorepo/keying-shared'
import { ThemePalette } from '@angular/material/core'

@Component({
    selector: 'app-kanban-card',
    templateUrl: './kanban-card.component.html',
    styleUrls: ['./kanban-card.component.scss'],
    animations: [fadeIn, slideInOnlyUpStaggered],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class KanbanCardComponent implements OnInit, OnChanges, OnDestroy {
  @Input() items: Structure[]
  @Input() keySystem: KeySystem
  @Input() selectedUsers: User[]
  @Input() users: User[]
  @Input() user: User

  destroy$ = new Subject()
  apiSubscription: Subscription | null = null
  interval = 10

  userReports = []
  userPendingReports = []
  userCompletedReports = []
  userFailedReports = []

  constructor(
    private router: Router,
    private keyingStructureService: KeyingStructureService,
    private keyingSystemService: KeyingSystemService,
    private loadingService: LoadingService,
    private dialogService: DialogService,
    private errorService: ErrorService,
    private keyingReportsService: KeyingReportsService,
    private reportsService: ReportsService,
    private toastService: ToastService,
    private cdr: ChangeDetectorRef
  ) {}

  /* istanbul ignore next */
  ngOnInit(): void {
    this.items = this.items.filter((element) => element.keySystems.some((subElement) => subElement.isRejected !== true))
  }

  /* istanbul ignore next */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes.selectedUsers && changes.selectedUsers.currentValue?.length) {
      this.getReportStatus(this.selectedUsers)
    }
  }

  /* istanbul ignore next */
  ngOnDestroy() {
    this.destroy$.next(true)
    this.destroy$.complete()
    if (this.apiSubscription) {
      this.apiSubscription.unsubscribe()
    }
  }

  /* istanbul ignore next */
  initials(name) {
    const initials = name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
    return initials
  }

  /* istanbul ignore next */
  changeUser(structure, userId) {
    const request = [
      {
        op: 'replace',
        path: '/lockedById',
        value: userId,
      },
    ]

    this.keyingStructureService
      .patchStructure(structure.id, request)
      .pipe(take(1))
      .subscribe(
        (structure) => {
          this.errorService.confirm('Updated Structure')
          this.loadingService.set({ isLoading: false, message: '' })
        },
        (error) => {
          this.errorService.throw(error)
        }
      )
  }

  isPublishable(structure: Structure) {
    const unpublished = structure.keySystems.filter((keySystem: KeySystem) => keySystem.statusId !== 13)
    let isPublishable = unpublished.length > 0

    unpublished.forEach((keySystem: KeySystem) => {
      if (keySystem.statusId !== 10 && keySystem.statusId !== 12) {
        isPublishable = false
      }
    })

    return isPublishable
  }

  isOrderIdPresent(structure: Structure) {
    const orderNumber = structure.keySystems.filter((x) => x.orderNumber != '').length > 0
    return orderNumber
  }

  /* istanbul ignore next */
  publish(structure: Structure) {
    this.loadingService.set({ isLoading: true, message: 'Publishing Structure' })

    this.keyingSystemService
      .publishStructure(structure.id)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.loadingService.set({ isLoading: false, message: '' })
          this.keyingStructureService.loadMappedStructures()
          const data = {
            title: 'Publish Success',
            confirm: 'View Report',
            cancel: 'Ok',
            showCancel: true,
            successMsg: true,
            message: `Structure ${structure.structureNumber} successfully published.`,
          }
          const dialogRef = this.dialogService.open(ConfirmDialogComponent, data)
          dialogRef.afterClosed().subscribe((result) => {
            if (result.confirmed) {
              if (structure.keySystems.length === 1) {
                this.router.navigate([`/Structures/${structure.id}/Systems/${structure.keySystems[0].id}/Reports`])
                this.keyingStructureService.selectStructure(structure)
              } else {
                this.router.navigate([`/Structures/${structure.id}/Systems`])
              }
            } else {
              location.reload()
            }
          })
        },
        error: (error) => {
          this.loadingService.set({ isLoading: false, message: '' })
          this.errorService.throw(error)
        },
      })
  }

  /* istanbul ignore next */
  overrideKeySystemErrorsConfirm(structureId: number, keySystemId: number, tmkName) {
    if (tmkName) {
      let data = {
        title: 'Override Errors',
        message: `Are you sure you want to override error report for system ${tmkName}?`,
        cancel: 'Cancel',
        confirm: 'Override',
        showCancel: true,
        reasonRequired: false,
      }

      let dialogRef = this.dialogService.open(ConfirmDialogComponent, data, false)

      dialogRef.afterClosed().subscribe((result) => {
        if (result.confirmed) {
          this.overrideMECSErrors(structureId, keySystemId)
        }
      })
    }
  }

  /* istanbul ignore next */
  overrideMECSErrors(structureId: number, keySystemId: number) {
    this.loadingService.set({
      isLoading: true,
      message: 'Overriding MECS errors, would publish if it is only independent system',
    })

    this.keyingSystemService
      .overrideMECSErrors(structureId, keySystemId)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.loadingService.set({ isLoading: false, message: '' })
          this.keyingStructureService.loadMappedStructures()
        },
        error: (error) => {
          this.errorService.throw(error)
        },
      })
  }

  canOverideAndPublish(structure: Structure) {
    const keySystemsYetToPublish = structure.keySystems.filter(
      (keySystem: KeySystem) => keySystem.statusId === 9 || keySystem.statusId === 10 || keySystem.statusId === 12
    )

    let isOverrideAndPublish = keySystemsYetToPublish.length > 0

    keySystemsYetToPublish.forEach((keySystem: KeySystem) => {
      if (keySystem.statusId !== 9) {
        isOverrideAndPublish = false
      }
    })

    return isOverrideAndPublish
  }

  /* istanbul ignore next */
  overrideStructureErrorsConfirm(structure: Structure) {
    let data = {
      title: 'Override Errors',
      message: `Are you sure you want to override errors report for ${structure.structureNumber}?`,
      cancel: 'Cancel',
      confirm: 'Override',
      showCancel: true,
      reasonRequired: false,
    }

    let dialogRef = this.dialogService.open(ConfirmDialogComponent, data, false)

    dialogRef.afterClosed().subscribe((result) => {
      if (result.confirmed) {
        this.overrideMECSErrorsAndPublish(structure.id)
      }
    })
  }

  /* istanbul ignore next */
  overrideMECSErrorsAndPublish(structureId) {
    this.loadingService.set({ isLoading: true, message: 'Publishing Structure' })

    this.keyingSystemService
      .overrideMECSErrorsAndPublish(structureId)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.loadingService.set({ isLoading: false, message: '' })
          this.keyingStructureService.loadMappedStructures()
        },
        error: (error) => {
          this.loadingService.set({ isLoading: false, message: '' })
          this.errorService.throw(error)
        },
      })
  }

  /* istanbul ignore next */
  rejectConfirm(structure: Structure) {
    let data = {
      title: 'Reject Order',
      message: `Are you sure you want to reject this order?`,
      cancel: 'Cancel',
      confirm: 'Reject',
      showCancel: true,
      reasonRequired: false,
    }
    const structureId = structure.id
    const orderId = structure.keySystems.filter((x) => x.orderNumber != '')?.[0]?.orderId
    const orderNumber = structure.keySystems?.[0]?.orderNumber

    let dialogRef = this.dialogService.open(ConfirmDialogComponent, data, false)

    dialogRef.afterClosed().subscribe((result) => {
      if (result.confirmed) {
        this.loadingService.set({ isLoading: true, message: 'Rejecting Order' })
        this.keyingStructureService.rejectOrder(structureId, orderId, false).subscribe(
          (res) => {
            this.errorService.confirm(`Order ${orderNumber} was successfully rejected`)
            this.keyingStructureService.loadMappedStructures()
            this.loadingService.set({ isLoading: false, message: '' })
          },
          (error) => {
            this.loadingService.set({ isLoading: false, message: '' })
            this.errorService.throw(error)
          }
        )
      }
    })
  }

  /* istanbul ignore next */
  deleteConfirm(structure: Structure) {
    let data = {
      title: 'Reject & Delete Order',
      message: `Are you sure you want to reject this order and delete all associated data from the structure?`,
      cancel: 'Cancel',
      confirm: 'Reject & Delete',
      showCancel: true,
      reasonRequired: false,
    }

    const structureId = structure.id
    const orderId = structure.keySystems.filter((x) => x.orderNumber != '')?.[0]?.orderId
    const orderNumber = structure.keySystems?.[0]?.orderNumber

    let dialogRef = this.dialogService.open(ConfirmDialogComponent, data, false)

    dialogRef.afterClosed().subscribe((result) => {
      if (result.confirmed) {
        this.loadingService.set({ isLoading: true, message: 'Rejecting and Deleting Order' })
        this.keyingStructureService.rejectOrder(structureId, orderId, true).subscribe(
          (res) => {
            this.errorService.confirm(`Order ${orderNumber} was successfully rejected and all associated data deleted`)
            this.keyingStructureService.loadMappedStructures()
            this.loadingService.set({ isLoading: false, message: '' })
          },
          (error) => {
            this.loadingService.set({ isLoading: false, message: '' })
            this.errorService.throw(error)
          }
        )
      }
    })
  }

  trackByStructureId(_index: number, item: Structure): number {
    return item.id
  }

  changeUserDisabled(item) {
    for (let keySystem of item.keySystems) {
      if (keySystem.statusId === 6 || keySystem.statusId === 7) {
        return true
      }
    }
  }

  /* istanbul ignore next */
  selectStructure(structure: Structure) {
    this.keyingStructureService
      .getStructure(structure.id)
      .pipe(take(1))
      .subscribe((strut) => {
        this.keyingStructureService.selectStructure(strut)

        if (!structure.city) {
          const dialogRef = this.dialogService.open(NewStructureDialogComponent, structure)

          dialogRef.afterClosed().subscribe((structure) => {
            if (structure) {
              this.keyingStructureService.updateStructure(structure)
            }
          })
        } else {
          if (structure.keySystems.length === 1) {
            this.keyingSystemService.selectKeySystem(structure.keySystems[0])
            this.router.navigate([AppRoutingConfig.getStructureUrl(structure.id, structure.keySystems[0].id)])
          } else {
            this.router.navigate([AppRoutingConfig.getStructureUrlNew(structure.id)])
          }
        }
      })
  }

  /* istanbul ignore next */
  selectKeySystem(structure: Structure, keySystem: KeySystem) {
    this.keyingStructureService.selectStructure(structure)
    this.keyingSystemService.selectKeySystem(keySystem)
    this.router.navigate([AppRoutingConfig.getStructureUrl(structure.id, keySystem.id)])
  }

  /* istanbul ignore next */
  downloadReport(structure: Structure, keySystem) {
    this.loadingService.set({
      isLoading: true,
      message: 'Getting MECS Report',
    })
    this.keyingSystemService.getMECSReport(structure.id, keySystem.id).subscribe((res) => {
      // It is necessary to create a new blob object with mime-type explicitly set
      // otherwise only Chrome works like it should
      var newBlob = new Blob([res], { type: 'application/pdf' })

      // IE doesn't allow using a blob object directly as link href
      // instead it is necessary to use msSaveOrOpenBlob
      if (window.navigator && (window.navigator as any).msSaveOrOpenBlob) {
        ;(window.navigator as any).msSaveOrOpenBlob(newBlob)
        return
      }

      // For other browsers:
      // Create a link pointing to the ObjectURL containing the blob.
      const data = window.URL.createObjectURL(newBlob)

      var link = document.createElement('a')
      link.href = data
      link.download = `${structure.structureNumber}.${keySystem.topMasterKeyName}.MECS.${moment().format('MMDDYY')}.${
        res.type.split('/')[1]
      }`
      // this is necessary as link.click() does not work on the latest firefox
      link.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }))

      setTimeout(() => {
        // For Firefox it is necessary to delay revoking the ObjectURL
        window.URL.revokeObjectURL(data)
        link.remove()
        this.loadingService.set({
          isLoading: false,
          message: '',
        })
      }, 100)
    })
  }

  // TODO: Takeoff Report
  /* istanbul ignore next */
  downloadHistoryTakeOffReport(structure: Structure, keySystem) {
    this.loadingService.set({
      isLoading: true,
      message: 'Getting History Report',
    })

    this.keyingReportsService
      .getOrders(keySystem.id)
      .pipe(take(1))
      .subscribe(
        (order) => {
          this.keyingReportsService.getHistoryTakeOffReport(structure.id, keySystem.id, order.id, false).subscribe(
            (res) => {
              // It is necessary to create a new blob object with mime-type explicitly set
              // otherwise only Chrome works like it should
              var newBlob = new Blob([res], { type: 'application/pdf' })
              // IE doesn't allow using a blob object directly as link href
              // instead it is necessary to use msSaveOrOpenBlob
              if (window.navigator && (window.navigator as any).msSaveOrOpenBlob) {
                ;(window.navigator as any).msSaveOrOpenBlob(newBlob)
                return
              }
              // For other browsers:
              // Create a link pointing to the ObjectURL containing the blob.
              const data = window.URL.createObjectURL(newBlob)
              var link = document.createElement('a')
              link.href = data
              link.download = `${structure.structureNumber}.${
                keySystem.topMasterKeyName
              }.OrderDetails.${moment().format('MMDDYY')}.${res.type.split('/')[1]}`
              // this is necessary as link.click() does not work on the latest firefox
              link.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }))
              setTimeout(() => {
                // For Firefox it is necessary to delay revoking the ObjectURL
                window.URL.revokeObjectURL(data)
                link.remove()
                this.loadingService.set({
                  isLoading: false,
                  message: '',
                })
              }, 100)
            },
            (error) => {
              this.errorService.throw(error)
            }
          )
        },
        (error) => {
          this.errorService.throw(error)
        }
      )
  }

  /* istanbul ignore next */
  generateReport(structure, isRequeue): void {
    let reportType = Accept.OrderDetailReport
    let reportName = ReportName.OrderDetailReport
    let displayReportName = 'Order Detail Report'

    this.keyingReportsService
      .getOrders(structure.keySystems[0].id)
      .pipe(take(1))
      .subscribe((order) => {
        this.keyingReportsService
          .getHistoryTakeOffReport(structure.id, structure.keySystems[0].id, order.id, isRequeue)
          .pipe(take(1))
          .subscribe(() => {
            this.errorService.notify(
              'Generating Report',
              'This report will take some time to build. Please come back later to download.'
            )
            setTimeout(() => {
              this.toastService.close()
            }, 8000)
            this.getReportStatus(this.selectedUsers)
          })
      })
  }

  /* istanbul ignore next */
  downloadOrderDetailReport(structure: Structure, keySystem): void {
    let reportName
    let reportType

    this.reportsService
      .downloadReport(structure.id, keySystem.id, 8)
      .pipe(take(1))
      .subscribe({
        next: (file) => {
          // It is necessary to create a new blob object with mime-type explicitly set
          // otherwise only Chrome works like it should
          const newBlob = new Blob([file], { type: file.type })

          // IE doesn't allow using a blob object directly as link href
          // instead it is necessary to use msSaveOrOpenBlob
          if (window.navigator && (window.navigator as any).msSaveOrOpenBlob) {
            ;(window.navigator as any).msSaveOrOpenBlob(newBlob)
            return
          }

          reportName = `${structure.structureNumber}.${keySystem.topMasterKeyName}.OrderDetail.${moment().format(
            'MMDDYY'
          )}.${file.type.split('/')[1]}`

          // For other browsers:
          // Create a link pointing to the ObjectURL containing the blob.
          const data = window.URL.createObjectURL(newBlob)

          const link = document.createElement('a')
          link.href = data
          // link.download = `${this.structure.structureNumber}.${this.keySystem.topMasterKeyName}.${
          //   element.name
          // }.${moment().format('MMDDYY')}.${file.type.split('/')[1]}`
          link.download = reportName
          // this is necessary as link.click() does not work on the latest firefox
          link.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }))

          setTimeout(() => {
            // For Firefox it is necessary to delay revoking the ObjectURL
            window.URL.revokeObjectURL(data)
            link.remove()
          }, 100)
        },
        error: (error) => {
          this.errorService.throw(error)
        },
      })
  }

  /* istanbul ignore next */
  getReportStatus(usersSelected) {
    this.userReports = []
    this.userPendingReports = []
    this.userCompletedReports = []
    this.userFailedReports = []
    let ids
    let params
    if (!usersSelected) {
      params = []
    } else {
      ids = usersSelected.map((s) => (s = s.id))
      params = ids.map((i) => (i = { userids: i }))
    }

    this.reportsService.getUserReportStatus(params).subscribe((reportResponse: any) => {
      this.userReports = reportResponse.filter((x) => x.reportTypeId === 8)
      // Remove duplicates based on keysystemId
      this.userReports = this.userReports.filter(
        (x, i, arr) => arr.findIndex((y) => y.keysystemId === x.keysystemId) === i
      )
      this.userPendingReports = this.userReports.filter((x) => x.statusId === 1 || x.statusId === 2)
      this.userCompletedReports = this.userReports.filter((x) => x.statusId === 3)
      this.userFailedReports = this.userReports.filter((x) => x.statusId === 4)

      this.userReports.forEach((report: any) => {
        let existReport = reportResponse.filter((x: any) => x.reportTypeId == report.reportTypeId)
        if (existReport.length > 0) {
          report.status = existReport[0].statusId
          // this.shouldScheduleRefresh(existReport)
        }
      })
      setTimeout(() => {
        this.shouldScheduleRefresh(this.userPendingReports)
      }, 4000)
      this.cdr.markForCheck()
    })
  }

  isPendingReport(structure: Structure) {
    const keySystemReportPending = this.userPendingReports.filter((x) => x.keysystemId === structure.keySystems[0].id)

    let isPending = keySystemReportPending.length > 0

    return isPending
  }

  isCompletedReport(structure: Structure) {
    const keySystemReportCompleted = this.userCompletedReports.filter(
      (x) => x.keysystemId === structure.keySystems[0].id
    )
    let isCompleted = keySystemReportCompleted.length > 0

    return isCompleted
  }

  isFailedReport(structure: Structure) {
    const keySystemReportFailed = this.userFailedReports.filter((x) => x.keysystemId === structure.keySystems[0].id)

    let isFailed = keySystemReportFailed.length > 0

    return isFailed
  }

  /* istanbul ignore next */
  scheduleRefreshReports() {
    const schedule = interval(this.interval * 1000)
    this.apiSubscription = schedule.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.getReportStatus(this.selectedUsers)
    })
  }

  /* istanbul ignore next */
  shouldScheduleRefresh(reports) {
    if (environment.flags.autoReload) {
      let shouldSchedule = false
      reports.forEach((report) => {
        if (report.statusId === 1 || report.statusId === 2) {
          shouldSchedule = true
        }
      })

      if (!this.apiSubscription && shouldSchedule) {
        this.scheduleRefreshReports()
      } else if (this.apiSubscription && !shouldSchedule) {
        this.apiSubscription.unsubscribe()
        this.apiSubscription = null
      }
    }
  }
}
